import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox, Text } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, PORTAL_HEIGHT, FLOOR_HEIGHT, TILE_COLORS } from '../constants';
import { SparkleParticles } from '../shared/Particles3D';

interface PortalTile3DProps {
  position: [number, number, number];
  variant: 'start' | 'end';
  entranceDelay?: number;
}

// Pulsing arrow
function ExitArrow({ offset, rotation, color, tileY }: {
  offset: [number, number, number]; rotation: number; color: string; tileY: number;
}) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();
    const pulse = Math.sin(t * 4) * 0.05;
    meshRef.current.position.set(
      offset[0] * (1 - pulse),
      tileY + PORTAL_HEIGHT / 2 + 0.02,
      offset[2] * (1 - pulse),
    );
    (meshRef.current.material as THREE.MeshStandardMaterial).opacity = 0.5 + Math.sin(t * 3) * 0.3;
  });

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, rotation, 0]}>
      <coneGeometry args={[0.08, 0.18, 4]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.5}
        transparent
        opacity={0.6}
      />
    </mesh>
  );
}

// Upward light beam
function BeaconBeam({ color, tileY }: { color: string; tileY: number }) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();
    const mat = meshRef.current.material as THREE.MeshStandardMaterial;
    mat.opacity = 0.12 + Math.sin(t * 2) * 0.06;
  });

  return (
    <mesh ref={meshRef} position={[0, tileY + 1.5, 0]}>
      <cylinderGeometry args={[0.02, 0.2, 3, 8, 1, true]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.8}
        transparent
        opacity={0.15}
        side={THREE.DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
}

// Expanding ring animation for start tile
function ExpandingRing({ color, tileY }: { color: string; tileY: number }) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime * 1.5;
    meshRef.current.scale.setScalar(1 + (t % 2));
    (meshRef.current.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 1 - (t % 2));
  });

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, tileY + PORTAL_HEIGHT / 2 + 0.02, 0]}>
      <ringGeometry args={[TILE_SIZE * 0.25, TILE_SIZE * 0.35, 32]} />
      <meshBasicMaterial color={color} transparent opacity={0.5} side={THREE.DoubleSide} />
    </mesh>
  );
}

export default function PortalTile3D({ position, variant, entranceDelay = 0 }: PortalTile3DProps) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsed = useRef(0);
  const entered = useRef(false);

  const colors = variant === 'start' ? TILE_COLORS.start : TILE_COLORS.end;
  const tileY = FLOOR_HEIGHT + PORTAL_HEIGHT / 2;

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    elapsed.current += delta;

    if (!entered.current) {
      const t = Math.max(0, elapsed.current - entranceDelay);
      const progress = Math.min(t * 4, 1);
      groupRef.current.scale.set(progress, progress, progress);
      if (progress >= 1) entered.current = true;
    }
  });

  const sparkleColor = variant === 'start' ? '#4CAF50' : '#2196F3';

  return (
    <group ref={groupRef} position={[position[0], 0, position[2]]} scale={0}>
      {/* Raised platform */}
      <RoundedBox
        args={[TILE_SIZE * 0.85, PORTAL_HEIGHT, TILE_SIZE * 0.85]}
        radius={0.04}
        smoothness={3}
        position={[0, tileY, 0]}
      >
        <meshStandardMaterial
          color={colors.front}
          emissive={colors.glow}
          emissiveIntensity={0.3}
          metalness={0.15}
          roughness={0.3}
        />
      </RoundedBox>

      {/* Label */}
      <Text
        position={[0, tileY + PORTAL_HEIGHT / 2 + 0.01, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        fontSize={0.35}
        color="white"
        anchorX="center"
        anchorY="middle"
        fontWeight={700}
      >
        {variant === 'start' ? 'S' : 'E'}
      </Text>

      {/* Sparkle particles */}
      <group position={[0, tileY + PORTAL_HEIGHT / 2, 0]}>
        <SparkleParticles
          count={15}
          bounds={[TILE_SIZE * 0.8, 0.8, TILE_SIZE * 0.8] as [number, number, number]}
          color={sparkleColor}
        />
      </group>

      {/* Glow light */}
      <pointLight
        position={[0, tileY + 0.3, 0]}
        color={colors.glow}
        intensity={1.5}
        distance={2.5}
        decay={2}
      />

      {/* Start tile: expanding ring */}
      {variant === 'start' && (
        <ExpandingRing color={colors.glow} tileY={tileY} />
      )}

      {/* End tile extras */}
      {variant === 'end' && (
        <>
          <ExitArrow offset={[0, 0, -0.55]} rotation={0} color={colors.glow} tileY={tileY} />
          <ExitArrow offset={[0, 0, 0.55]} rotation={Math.PI} color={colors.glow} tileY={tileY} />
          <ExitArrow offset={[-0.55, 0, 0]} rotation={Math.PI / 2} color={colors.glow} tileY={tileY} />
          <ExitArrow offset={[0.55, 0, 0]} rotation={-Math.PI / 2} color={colors.glow} tileY={tileY} />
          <BeaconBeam color={colors.glow} tileY={tileY} />
        </>
      )}
    </group>
  );
}
